import { useState, useRef, useEffect } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { Package, Truck, Tag, CheckCircle, AlertTriangle, Printer, Search, FileText, Download, ClipboardCopy } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import jsPDF from "jspdf";
import QRCode from "qrcode";
import { PublicSignatureQr } from "../components/PublicSignatureQr";

// ═══════════════════════════════════════════════════════════════════
// UTILITIES
// ═══════════════════════════════════════════════════════════════════
function formatDate(d: any) {
  if (!d) return "-";
  const date = new Date(d);
  return date.toLocaleDateString("pt-BR") + " " + date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

async function gerarEtiquetaPDF(loteNumero: number, emitenteNome: string) {
  // Etiqueta 10x15cm (100mm x 150mm)
  const doc = new jsPDF({ unit: "mm", format: [100, 150] });

  // Gerar QR Code como data URL
  const qrDataUrl = await QRCode.toDataURL(String(loteNumero), { width: 200, margin: 1 });

  // Título
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("LOTE DE RETIRADA", 50, 15, { align: "center" });

  // Emitente
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(emitenteNome, 50, 25, { align: "center" });

  // Número do lote grande
  doc.setFontSize(36);
  doc.setFont("helvetica", "bold");
  doc.text(String(loteNumero), 50, 55, { align: "center" });

  // Label
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text("Lote Nº", 50, 42, { align: "center" });

  // QR Code
  doc.addImage(qrDataUrl, "PNG", 25, 65, 50, 50);

  // Rodapé
  doc.setFontSize(8);
  doc.text(`Gerado em: ${new Date().toLocaleDateString("pt-BR")} ${new Date().toLocaleTimeString("pt-BR")}`, 50, 130, { align: "center" });
  doc.text("box-warehouse.com.br", 50, 140, { align: "center" });

  doc.save(`lote_${loteNumero}.pdf`);
}

// ═══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════
export default function Expedicao() {
  const { user } = useGoogleAuth();
  const isAdmin = user?.role === "admin";

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      <h1 className="text-2xl font-bold mb-4">Controle de Expedição</h1>
      <Tabs defaultValue="retiradas">
        <TabsList>
          <TabsTrigger value="pedidos"><Package className="w-4 h-4 mr-1" />Pedidos a Retirar</TabsTrigger>
          <TabsTrigger value="retiradas"><Truck className="w-4 h-4 mr-1" />Retiradas MECOM</TabsTrigger>
        </TabsList>
        <TabsContent value="pedidos"><PedidosRetirarTab /></TabsContent>
        <TabsContent value="retiradas"><RetiradasTab user={user} isAdmin={isAdmin} /></TabsContent>
      </Tabs>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// COMPONENTE: CANVAS DE ASSINATURA DIGITAL
// ═══════════════════════════════════════════════════════════════════
function SignatureCanvas({ onSignature }: { onSignature: (base64: string) => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawing = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#000000";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
  }, []);

  const getPos = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    if ("touches" in e) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: (e as React.MouseEvent).clientX - rect.left, y: (e as React.MouseEvent).clientY - rect.top };
  };

  const startDraw = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    isDrawing.current = true;
    const ctx = canvasRef.current!.getContext("2d")!;
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (!isDrawing.current) return;
    const ctx = canvasRef.current!.getContext("2d")!;
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const endDraw = () => {
    isDrawing.current = false;
    const canvas = canvasRef.current!;
    onSignature(canvas.toDataURL("image/png"));
  };

  const clear = () => {
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext("2d")!;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    onSignature("");
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={400}
        height={150}
        className="border rounded cursor-crosshair touch-none w-full"
        onMouseDown={startDraw}
        onMouseMove={draw}
        onMouseUp={endDraw}
        onMouseLeave={endDraw}
        onTouchStart={startDraw}
        onTouchMove={draw}
        onTouchEnd={endDraw}
      />
      <Button variant="ghost" size="sm" className="mt-1 text-xs" onClick={clear}>Limpar assinatura</Button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// TAB: PEDIDOS A RETIRAR
// ═══════════════════════════════════════════════════════════════════
function PedidosRetirarTab() {
  const [search, setSearch] = useState("");
  const [filtroTransportadora, setFiltroTransportadora] = useState<string>("__all__");
  const [filtroTipoFrete, setFiltroTipoFrete] = useState<string>("__all__");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [motoristaNome, setMotoristaNome] = useState("");
  const [motoristaDocumento, setMotoristaDocumento] = useState("");
  const [placaVeiculo, setPlacaVeiculo] = useState("");
  const [assinaturaBase64, setAssinaturaBase64] = useState("");
  const [relatorioModalOpen, setRelatorioModalOpen] = useState(false);

  const { data: pedidos, refetch } = trpc.pedidos.listOpen.useQuery();
  const utils = trpc.useUtils();
  const finalizarMutation = trpc.expedicao.finalizarExpedicao.useMutation({
    onSuccess: () => {
      toast.success("Expedição finalizada com sucesso!");
      setShowModal(false);
      setSelectedIds([]);
      setMotoristaNome("");
      setMotoristaDocumento("");
      setPlacaVeiculo("");
      setAssinaturaBase64("");
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  // Filtrar apenas pedidos com status 'aguardando expedição'
  const pedidosExpedicao = (pedidos || []).filter((p: any) => p.status === "aguardando expedição");

  // Aplicar busca
  const pedidosFiltrados = pedidosExpedicao.filter((p: any) => {
    const matchSearch = !search || 
      String(p.id).includes(search) ||
      (p.destinatarioNome || "").toLowerCase().includes(search.toLowerCase());
    const matchTransp = filtroTransportadora === "__all__" || (p.transportadoraNome || "") === filtroTransportadora;
    const matchFrete = filtroTipoFrete === "__all__" || (p.tipoFrete || "") === filtroTipoFrete;
    return matchSearch && matchTransp && matchFrete;
  });

  // Extrair valores únicos para filtros
  const transportadoras = [...new Set(pedidosExpedicao.map((p: any) => p.transportadoraNome).filter(Boolean))];
  const tiposFrete = [...new Set(pedidosExpedicao.map((p: any) => p.tipoFrete).filter(Boolean))];

  const formatarEndereco = (p: any) => {
    const campos = p.tipoEntrega === "LEVAR_ATE_TRANSPORTADORA"
      ? (p.transportadoraEntregaLogradouro
        ? [p.transportadoraEntregaLogradouro, p.transportadoraEntregaNumero, p.transportadoraEntregaComplemento, p.transportadoraEntregaBairro, p.transportadoraEntregaCidade, p.transportadoraEntregaUf, p.transportadoraEntregaCep]
        : [p.transportadoraLogradouro, p.transportadoraNumero, p.transportadoraComplemento, p.transportadoraBairro, p.transportadoraCidade, p.transportadoraUf, p.transportadoraCep])
      : p.tipoEntrega === "LEVAR_ATE_DESTINATARIO"
        ? (p.destinatarioEntregaLogradouro
          ? [p.destinatarioEntregaLogradouro, p.destinatarioEntregaNumero, p.destinatarioEntregaComplemento, p.destinatarioEntregaBairro, p.destinatarioEntregaCidade, p.destinatarioEntregaUf, p.destinatarioEntregaCep]
          : [p.destinatarioLogradouro, p.destinatarioNumero, p.destinatarioComplemento, p.destinatarioBairro, p.destinatarioCidade, p.destinatarioUf, p.destinatarioCep])
        : [];
    const [logradouro, numero, complemento, bairro, cidade, uf, cep] = campos;
    const primeiraLinha = [logradouro, numero].filter(Boolean).join(", ");
    return [primeiraLinha, complemento, bairro, [cidade, uf].filter(Boolean).join("/"), cep].filter(Boolean).join(" - ") || "-";
  };

  const copiarRelacaoParaTransportadora = async () => {
    const pedidosSelecionados = pedidosExpedicao.filter((p: any) => selectedIds.includes(p.id));
    if (pedidosSelecionados.length === 0) {
      toast.error("Selecione pelo menos um pedido");
      return;
    }

    const relacao = pedidosSelecionados.map((p: any) => {
      const levarAteTransportadora = p.tipoEntrega === "LEVAR_ATE_TRANSPORTADORA" || p.tipoEntrega === "AGUARDAR_AGENDAMENTO";
      const linhas = [
        `PEDIDO #${p.id}`,
        `NOTA FISCAL: ${p.numeroNfe || "-"}`,
        `DATA: ${p.dataFaturamento ? new Date(p.dataFaturamento).toLocaleDateString("pt-BR") : "-"}`,
        `DESTINATÁRIO: ${p.destinatarioNome || "-"}`,
        `VOLUMES: ${p.volumes ?? "-"}`,
        `PESO: ${p.pesoBruto ?? "-"} kg`,
        `ENTREGA: ${levarAteTransportadora ? "LEVAR ATÉ A TRANSPORTADORA" : "LEVAR ATÉ O DESTINATÁRIO"}`,
      ];

      if (levarAteTransportadora) {
        const enderecoTransportadora = formatarEndereco(p.tipoEntrega === "AGUARDAR_AGENDAMENTO" ? { ...p, tipoEntrega: "LEVAR_ATE_TRANSPORTADORA" } : p);
        linhas.push(`TRANSPORTADORA: ${p.transportadoraNome || "-"}`);
        linhas.push(`ENDEREÇO DA TRANSPORTADORA: ${enderecoTransportadora}`);
      } else {
        linhas.push(`ENDEREÇO: ${formatarEndereco(p)}`);
      }

      return linhas.join("\n");
    }).join("\n_________________________________________________________\n\n");

    try {
      await navigator.clipboard.writeText(relacao);
      toast.success(`Relação de ${pedidosSelecionados.length} pedido(s) copiada`);
    } catch {
      toast.error("Não foi possível copiar a relação");
    }
  };

  const baixarDanfesVendaCce = async () => {
    try {
      const result = await utils.pedidos.getDanfesVendaCceZip.fetch({ pedidoIds: selectedIds });
      const byteChars = atob(result.base64);
      const byteArray = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) byteArray[i] = byteChars.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([byteArray], { type: "application/zip" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = result.filename;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(`ZIP de ${selectedIds.length} pedido(s) baixado`);
    } catch (err: any) {
      toast.error(err.message || "Não foi possível baixar as DANFEs e CC-e.");
    }
  };

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const toggleAll = () => {
    if (selectedIds.length === pedidosFiltrados.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(pedidosFiltrados.map((p: any) => p.id));
    }
  };

  const handleFinalizar = () => {
    if (selectedIds.length === 0) {
      toast.error("Selecione pelo menos um pedido");
      return;
    }
    setShowModal(true);
  };

  const handleConfirmar = () => {
    if (!motoristaNome.trim()) { toast.error("Informe o nome do motorista"); return; }
    if (!motoristaDocumento.trim()) { toast.error("Informe o documento do motorista"); return; }
    if (!placaVeiculo.trim()) { toast.error("Informe a placa do veículo"); return; }
    if (!assinaturaBase64) { toast.error("Assinatura digital é obrigatória"); return; }
    finalizarMutation.mutate({
      pedidoIds: selectedIds,
      motoristaNome: motoristaNome.trim(),
      motoristaDocumento: motoristaDocumento.trim(),
      placaVeiculo: placaVeiculo.trim().toUpperCase(),
      assinaturaBase64,
    });
  };

  return (
    <div className="space-y-4 mt-4">
      {/* Busca e filtros */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2 top-2.5 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por ID do pedido, destinatário..."
            className="pl-8 w-[280px]"
          />
        </div>
        <Select value={filtroTransportadora} onValueChange={setFiltroTransportadora}>
          <SelectTrigger className="w-[200px]"><SelectValue placeholder="Transportadora" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todas transportadoras</SelectItem>
            {transportadoras.map((t: any) => (
              <SelectItem key={t} value={t}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filtroTipoFrete} onValueChange={setFiltroTipoFrete}>
          <SelectTrigger className="w-[150px]"><SelectValue placeholder="Tipo Frete" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos fretes</SelectItem>
            {tiposFrete.map((f: any) => (
              <SelectItem key={f} value={f}>{f}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {selectedIds.length > 0 && (
          <>
            <Button onClick={copiarRelacaoParaTransportadora} variant="outline">
              <ClipboardCopy className="w-4 h-4 mr-1" />
              Copiar relação para transportadora ({selectedIds.length})
            </Button>
            <Button onClick={baixarDanfesVendaCce} variant="outline">
              <Download className="w-4 h-4 mr-1" />
              Baixar DANFEs e CC-e ({selectedIds.length})
            </Button>
            <Button onClick={handleFinalizar} className="bg-green-600 hover:bg-green-700 text-white">
              <CheckCircle className="w-4 h-4 mr-1" />
              Finalizar Expedição ({selectedIds.length})
            </Button>
          </>
        )}
        <Button variant="outline" className="text-blue-600 border-blue-300 hover:bg-blue-50" onClick={() => setRelatorioModalOpen(true)}>
          <FileText className="w-4 h-4 mr-1" />
          Relatório
        </Button>
      </div>

      {/* Tabela */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm border">
          <thead className="bg-muted">
            <tr>
              <th className="p-2 text-left w-8">
                <input type="checkbox" onChange={toggleAll} checked={selectedIds.length > 0 && selectedIds.length === pedidosFiltrados.length} />
              </th>
              <th className="p-2 text-left">Pedido</th>
              <th className="p-2 text-left">Número da Nota</th>
              <th className="p-2 text-left">Data</th>
              <th className="p-2 text-left">Destinatário</th>
              <th className="p-2 text-left">Cidade/UF</th>
              <th className="p-2 text-left">Endereço</th>
              <th className="p-2 text-left">Volumes</th>
              <th className="p-2 text-left">Peso</th>
              <th className="p-2 text-left">Transportadora</th>
              <th className="p-2 text-left">Tipo de Entrega</th>
            </tr>
          </thead>
          <tbody>
            {pedidosFiltrados.map((p: any) => (
              <tr key={p.id} className="border-t hover:bg-muted/50">
                <td className="p-2">
                  <input type="checkbox" checked={selectedIds.includes(p.id)} onChange={() => toggleSelect(p.id)} />
                </td>
                <td className="p-2 font-bold">#{p.id}</td>
                <td className="p-2">{p.numeroNfe || "-"}</td>
                <td className="p-2">{p.dataPedido ? new Date(p.dataPedido).toLocaleDateString("pt-BR") : "-"}</td>
                <td className="p-2">{p.destinatarioNome || "-"}</td>
                <td className="p-2">{p.destinatarioCidade || "-"}/{p.destinatarioUf || "-"}</td>
                <td className="p-2">{formatarEndereco(p)}</td>
                <td className="p-2">{p.volumes || "-"}</td>
                <td className="p-2">{p.pesoBruto ?? "-"}</td>
                <td className="p-2">{p.transportadoraNome || "-"}</td>
                <td className="p-2">{p.tipoEntrega || "-"}</td>
              </tr>
            ))}
            {pedidosFiltrados.length === 0 && (
              <tr><td colSpan={11} className="p-4 text-center text-muted-foreground">Nenhum pedido aguardando expedição.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal Relatório */}
      <Dialog open={relatorioModalOpen} onOpenChange={setRelatorioModalOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Gerar Relatório</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Gerar relatório dos <strong>{pedidosFiltrados.length}</strong> pedidos filtrados em tela.
          </p>
          <DialogFooter className="flex gap-2 mt-4">
            <Button variant="outline" onClick={() => {
              import('xlsx').then(XLSX => {
                const rows = pedidosFiltrados.map((p: any) => ({
                  Número: p.numeroPedido,
                  Data: p.dataPedido ? new Date(p.dataPedido).toLocaleDateString('pt-BR') : '-',
                  Destinatário: p.destinatarioNome || '-',
                  'Cidade/UF': `${p.destinatarioCidade || '-'}/${p.destinatarioUf || '-'}`,
                  'Valor (R$)': parseFloat(p.valorTotal || 0).toFixed(2),
                  Volumes: p.volumes || '-',
                  Transportadora: p.transportadora || '-',
                  'Tipo Frete': p.tipoFrete || '-',
                  'Criado por': p.criadoPorNome || '-',
                }));
                const ws = XLSX.utils.json_to_sheet(rows);
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, 'Expedição');
                XLSX.writeFile(wb, `relatorio_expedicao_${new Date().toISOString().slice(0,10)}.xlsx`);
                setRelatorioModalOpen(false);
              });
            }}>
              <Download className="h-4 w-4 mr-2" />
              Excel
            </Button>
            <Button onClick={() => {
              const doc = new jsPDF({ orientation: 'landscape' });
              const lm = 10;
              let y = 15;
              doc.setFontSize(12); doc.setFont('helvetica', 'bold');
              doc.text(`Relatório de Expedição — ${pedidosFiltrados.length} registros`, lm, y); y += 8;
              doc.setFontSize(7); doc.setFont('helvetica', 'bold');
              const cols = ['Nº', 'Data', 'Destinatário', 'Cidade/UF', 'Valor', 'Volumes', 'Transportadora', 'T.Frete', 'Criado por'];
              const colW = [12, 18, 45, 22, 22, 14, 40, 18, 30];
              let x = lm;
              cols.forEach((c, i) => { doc.text(c, x, y); x += colW[i]; }); y += 5;
              doc.setFont('helvetica', 'normal');
              pedidosFiltrados.forEach((p: any) => {
                if (y > 190) { doc.addPage(); y = 15; }
                x = lm;
                const vals = [
                  String(p.numeroPedido || '-'),
                  p.dataPedido ? new Date(p.dataPedido).toLocaleDateString('pt-BR') : '-',
                  String(p.destinatarioNome || '-').slice(0, 24),
                  `${p.destinatarioCidade || '-'}/${p.destinatarioUf || '-'}`,
                  `R$ ${parseFloat(p.valorTotal || 0).toFixed(2)}`,
                  String(p.volumes || '-'),
                  String(p.transportadora || '-').slice(0, 22),
                  String(p.tipoFrete || '-').slice(0, 10),
                  String(p.criadoPorNome || '-').slice(0, 16),
                ];
                vals.forEach((v, i) => { doc.text(v, x, y); x += colW[i]; });
                y += 5;
              });
              doc.save(`relatorio_expedicao_${new Date().toISOString().slice(0,10)}.pdf`);
              setRelatorioModalOpen(false);
            }}>
              <FileText className="h-4 w-4 mr-2" />
              PDF
            </Button>
            <Button variant="outline" onClick={() => setRelatorioModalOpen(false)}>Cancelar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Finalizar Expedição */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-bold mb-4">Finalizar Expedição</h2>
            <p className="text-sm text-muted-foreground mb-4">{selectedIds.length} pedido(s) selecionado(s)</p>

            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium">Assinatura Digital</label>
                {!assinaturaBase64 && <PublicSignatureQr
                  key={selectedIds.join('-')}
                  tipo="EXPEDICAO"
                  titulo="Finalizar Expedição"
                  descricao={`${selectedIds.length} pedido(s) selecionado(s). Preencha os dados do motorista e assine.`}
                  referencia={{ pedidoIds: selectedIds }}
                  onSigned={(dados) => {
                    setMotoristaNome(dados.nomeAssinante);
                    setMotoristaDocumento(dados.documentoAssinante);
                    setPlacaVeiculo(dados.placaVeiculo || "");
                    setAssinaturaBase64(dados.assinaturaBase64);
                  }}
                />}
                {assinaturaBase64 && <div className="rounded border border-green-200 bg-green-50 p-3 text-sm text-green-700">Assinatura recebida. Confira os dados e confirme a expedição.</div>}
              </div>
            </div>

            <div className="flex gap-2 mt-6 justify-end">
              <Button variant="outline" onClick={() => setShowModal(false)}>Cancelar</Button>
              <Button onClick={handleConfirmar} disabled={finalizarMutation.isPending} className="bg-green-600 hover:bg-green-700 text-white">
                {finalizarMutation.isPending ? "Processando..." : "Confirmar"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// TAB: RETIRADAS MECOM
// ═══════════════════════════════════════════════════════════════════
function RetiradasTab({ user, isAdmin }: { user: any; isAdmin: boolean }) {
  const [emitenteId, setEmitenteId] = useState<string>("__all__");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("__all__");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [viewMode, setViewMode] = useState<"retiradas" | "lotes">("retiradas");
  const [loteStatusFilter, setLoteStatusFilter] = useState<string>("__all__");

  const { data: emitentes } = trpc.emitentes.list.useQuery();
  const emitenteIdNum = emitenteId !== "__all__" ? Number(emitenteId) : undefined;

  const { data: retiradas, refetch: refetchRetiradas } = trpc.expedicao.listRetiradas.useQuery({
    emitenteId: emitenteIdNum,
    search: search || undefined,
    status: statusFilter !== "__all__" ? statusFilter : undefined,
  });

  const { data: lotes, refetch: refetchLotes } = trpc.expedicao.listLotes.useQuery({
    emitenteId: emitenteIdNum,
    status: loteStatusFilter !== "__all__" ? loteStatusFilter : undefined,
  });

  const createLoteMutation = trpc.expedicao.createLote.useMutation({
    onSuccess: (data) => {
      toast.success(`Lote #${data.numero} criado com sucesso!`);
      setSelectedIds([]);
      refetchRetiradas();
      refetchLotes();
    },
    onError: (err) => toast.error(err.message),
  });

  const marcarRetiradoMutation = trpc.expedicao.marcarRetirado.useMutation({
    onSuccess: () => {
      toast.success("Lote marcado como retirado!");
      refetchRetiradas();
      refetchLotes();
    },
    onError: (err) => toast.error(err.message),
  });

  const handleCreateLote = () => {
    if (selectedIds.length === 0) {
      toast.error("Selecione pelo menos uma retirada");
      return;
    }
    createLoteMutation.mutate({
      emitenteId: emitenteIdNum,
      retiradaIds: selectedIds,
    });
  };

  const handleMarcarRetirado = (loteId: number) => {
    marcarRetiradoMutation.mutate({ loteId });
  };

  const handleGerarEtiqueta = async (lote: any) => {
    try {
      await gerarEtiquetaPDF(lote.numero, lote.emitenteNome || "Emitente");
      toast.success("Etiqueta gerada!");
    } catch (err) {
      toast.error("Erro ao gerar etiqueta");
    }
  };

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const toggleAll = () => {
    const pendentes = (retiradas || []).filter((r: any) => r.status === "pendente" && !r.loteId);
    if (selectedIds.length === pendentes.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(pendentes.map((r: any) => r.id));
    }
  };

  const getLotePendingDays = (lote: any) => {
    const criadoEm = new Date(lote.criadoEm);
    const agora = new Date();
    return Math.floor((agora.getTime() - criadoEm.getTime()) / (1000 * 60 * 60 * 24));
  };

  return (
    <div className="space-y-4 mt-4">
      <div className="flex flex-wrap gap-2 items-center">
        {isAdmin && (
          <Select value={emitenteId} onValueChange={setEmitenteId}>
            <SelectTrigger className="w-[200px]"><SelectValue placeholder="Emitente" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todos</SelectItem>
              {(emitentes as any[] || []).map((e: any) => (
                <SelectItem key={e.id} value={String(e.id)}>{e.razaoSocial}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <div className="flex gap-1">
          <button onClick={() => setViewMode("retiradas")} className={`px-3 py-1.5 rounded text-sm font-medium ${viewMode === "retiradas" ? "bg-orange-500 text-white" : "bg-muted"}`}>
            Retiradas
          </button>
          <button onClick={() => setViewMode("lotes")} className={`px-3 py-1.5 rounded text-sm font-medium ${viewMode === "lotes" ? "bg-orange-500 text-white" : "bg-muted"}`}>
            Lotes
          </button>
        </div>
      </div>

      {viewMode === "retiradas" && (
        <>
          <div className="flex flex-wrap gap-2 items-center">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por código..."
              className="w-[200px]"
            />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos</SelectItem>
                <SelectItem value="pendente">Pendente</SelectItem>
                <SelectItem value="retirado">Retirado</SelectItem>
              </SelectContent>
            </Select>
            {selectedIds.length > 0 && (
              <Button onClick={handleCreateLote} disabled={createLoteMutation.isPending}>
                <Tag className="w-4 h-4 mr-1" />
                Marcar como Lote ({selectedIds.length})
              </Button>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm border">
              <thead className="bg-muted">
                <tr>
                  <th className="p-2 text-left w-8">
                    <input type="checkbox" onChange={toggleAll} checked={selectedIds.length > 0 && selectedIds.length === (retiradas || []).filter((r: any) => r.status === "pendente" && !r.loteId).length} />
                  </th>
                  <th className="p-2 text-left">Código Pedido</th>
                  <th className="p-2 text-left">Loja</th>
                  <th className="p-2 text-left">Status</th>
                  <th className="p-2 text-left">Lote</th>
                  <th className="p-2 text-left">Criado Em</th>
                  <th className="p-2 text-left">Retirado Em</th>
                </tr>
              </thead>
              <tbody>
                {(retiradas || []).map((r: any) => (
                  <tr key={r.id} className="border-t hover:bg-muted/50">
                    <td className="p-2">
                      {r.status === "pendente" && !r.loteId && (
                        <input type="checkbox" checked={selectedIds.includes(r.id)} onChange={() => toggleSelect(r.id)} />
                      )}
                    </td>
                    <td className="p-2 font-mono text-xs">{r.codigoBip || "-"}</td>
                    <td className="p-2">{r.marketplaceNome || "-"}</td>
                    <td className="p-2">
                      <span className={`text-xs px-2 py-0.5 rounded ${r.status === "pendente" ? "bg-yellow-100 text-yellow-700" : "bg-green-100 text-green-700"}`}>
                        {r.status === "pendente" ? "Pendente" : "Retirado"}
                      </span>
                    </td>
                    <td className="p-2">
                      {r.loteNumero ? <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">#{r.loteNumero}</span> : "-"}
                    </td>
                    <td className="p-2">{formatDate(r.criadoEm)}</td>
                    <td className="p-2">{formatDate(r.retiradoEm)}</td>
                  </tr>
                ))}
                {(!retiradas || retiradas.length === 0) && (
                  <tr><td colSpan={7} className="p-4 text-center text-muted-foreground">Nenhuma retirada encontrada.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {viewMode === "lotes" && (
        <>
          <div className="flex flex-wrap gap-2 items-center">
            <Select value={loteStatusFilter} onValueChange={setLoteStatusFilter}>
              <SelectTrigger className="w-[150px]"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos</SelectItem>
                <SelectItem value="pendente">Pendente</SelectItem>
                <SelectItem value="retirado">Retirado</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm border">
              <thead className="bg-muted">
                <tr>
                  <th className="p-2 text-left">Lote Nº</th>
                  <th className="p-2 text-left">Emitente</th>
                  <th className="p-2 text-left">Qtd. Retiradas</th>
                  <th className="p-2 text-left">Status</th>
                  <th className="p-2 text-left">Criado Em</th>
                  <th className="p-2 text-left">Retirado Em</th>
                  <th className="p-2 text-left">Ações</th>
                </tr>
              </thead>
              <tbody>
                {(lotes || []).map((lote: any) => {
                  const pendingDays = getLotePendingDays(lote);
                  const isOverdue = lote.status === "pendente" && pendingDays > 15;
                  return (
                    <tr key={lote.id} className={`border-t hover:bg-muted/50 ${isOverdue ? "bg-red-50" : ""}`}>
                      <td className="p-2 font-bold">
                        #{lote.numero}
                        {isOverdue && <AlertTriangle className="w-4 h-4 inline ml-1 text-red-500" />}
                      </td>
                      <td className="p-2">{lote.emitenteNome || "-"}</td>
                      <td className="p-2">{lote.totalRetiradas || 0}</td>
                      <td className="p-2">
                        <span className={`text-xs px-2 py-0.5 rounded ${lote.status === "pendente" ? "bg-yellow-100 text-yellow-700" : "bg-green-100 text-green-700"}`}>
                          {lote.status === "pendente" ? "Pendente" : "Retirado"}
                        </span>
                        {isOverdue && <span className="text-xs text-red-600 ml-1">({pendingDays} dias)</span>}
                      </td>
                      <td className="p-2">{formatDate(lote.criadoEm)}</td>
                      <td className="p-2">{formatDate(lote.retiradoEm)}</td>
                      <td className="p-2">
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => handleGerarEtiqueta(lote)}>
                            <Printer className="w-3 h-3 mr-1" />Etiqueta
                          </Button>
                          {lote.status === "pendente" && (
                            <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-green-600" onClick={() => handleMarcarRetirado(lote.id)}>
                              <CheckCircle className="w-3 h-3 mr-1" />Retirado
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {(!lotes || lotes.length === 0) && (
                  <tr><td colSpan={7} className="p-4 text-center text-muted-foreground">Nenhum lote encontrado.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
